import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { queueApi } from '../services/api';
import toast from 'react-hot-toast';
import i18n from '../i18n';

export interface QueueConfig {
  id: string;
  name: string;
  isActive: boolean;
  operatingHours: any;
  estimatedServeTime: number;
  maxCapacity: number;
  currentNumber: number;
  currentServing: number;
  autoResetDaily: boolean;
  notifyBeforeCount: number;
  allowOnlineQueue: boolean;
  storeLogo: string | null;
  storeAddress: string | null;
  // LINE 推播設定
  lineChannelId: string | null;
  lineChannelSecret: string | null;
  lineAccessToken: string | null;
  lineEnabled: boolean;
  // LINE Login 設定
  lineLoginChannelId: string | null;
  lineLoginChannelSecret: string | null;
  lineLiffId: string | null;
  lineLoginEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

interface QueueState {
  config: QueueConfig | null;
  isLoading: boolean;
  hasFetched: boolean;
  error: string | null;

  fetchConfig: () => Promise<void>;
  updateConfig: (data: Partial<QueueConfig>) => Promise<void>;
  resetQueue: () => Promise<void>;
  toggleStatus: (isActive: boolean) => Promise<void>;
}

export const useQueueStore = create<QueueState>()(
  persist(
    (set, get) => ({
      config: null,
      isLoading: false,
      hasFetched: false,
      error: null,

      fetchConfig: async () => {
        const state = get();
        const shouldShowLoading = !state.config && !state.hasFetched;
        if (shouldShowLoading) {
          set({ isLoading: true, error: null });
        }

        try {
          const response = await queueApi.get();
          set({
            config: response.data,
            isLoading: false,
            hasFetched: true,
          });
        } catch (error: any) {
          console.error('Failed to fetch queue config:', error);
          set({
            error: error.message || '無法載入排隊設定',
            isLoading: false,
            hasFetched: true,
          });
        }
      },

      updateConfig: async (data) => {
        try {
          const response = await queueApi.update(data);
          set({ config: response.data });
          toast.success(i18n.t('common.messages.updateSuccess'));
        } catch (error: any) {
          const errorMsg = error.response?.data?.message || i18n.t('common.messages.updateFailed');
          toast.error(errorMsg);
          throw error;
        }
      },

      resetQueue: async () => {
        try {
          const response = await queueApi.reset();
          set({ config: response.data.config });
          toast.success(i18n.t('queue.messages.resetSuccess'));
        } catch (error: any) {
          const errorMsg = error.response?.data?.message || i18n.t('queue.messages.resetFailed');
          toast.error(errorMsg);
        }
      },

      toggleStatus: async (isActive) => {
        // 樂觀更新
        const previousConfig = get().config;
        if (previousConfig) {
          set({ config: { ...previousConfig, isActive } });
        }

        try {
          const response = await queueApi.toggleStatus(isActive);
          set({ config: response.data });
        } catch (error: any) {
          // 回滾
          if (previousConfig) {
            set({ config: previousConfig });
          }
          const errorMsg = error.response?.data?.message || '操作失敗';
          toast.error(errorMsg);
        }
      },
    }),
    {
      name: 'queue-config-storage',
      partialize: (state) => ({
        config: state.config,
        hasFetched: state.hasFetched,
      }),
    }
  )
);
